import { useEffect, useState } from "react";
import { BellRing, CalendarClock, LayoutList, Loader2, Pill, RotateCcw, Table2, Users, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import { errText, papi } from "../portalApi";
import { ClientList, FilterBar, applyFilter } from "./ClientList";
import { ClientProfile } from "./ClientProfile";
import { ClientTable } from "./ClientTable";
import { ClientModal, FollowUpModal, TemplateModal } from "./CrmModals";
import { fdate, inr } from "./crmUtil";

const Kpi = ({ icon: I, k, v, tone, testid }) => (
  <div className="flex items-center gap-3 rounded-xl border bg-white p-3" data-testid={testid}>
    <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${tone}`}><I className="h-4 w-4" /></div>
    <div className="min-w-0"><div className="truncate text-lg font-bold leading-none">{v}</div><div className="text-[11px] text-slate-500">{k}</div></div>
  </div>
);

const Digest = ({ d, onClose, onPick }) => (
  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="crm-digest-banner">
    <div className="flex items-center gap-2"><BellRing className="h-4 w-4 text-amber-600" /><b>Today's client reminders</b>
      <span className="text-xs text-slate-500">{d.refills} refill{d.refills === 1 ? "" : "s"} due this week · {d.followups} follow-up{d.followups === 1 ? "" : "s"}</span>
      <button onClick={onClose} className="ml-auto rounded p-1 hover:bg-amber-100" data-testid="crm-digest-close"><X className="h-4 w-4" /></button></div>
    {d.clients.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5">{d.clients.slice(0, 12).map((x) => (
      <button key={x.key || x.name} onClick={() => onPick(x.key || x.name.toUpperCase().split(/\s+/).join(" "))} className="rounded-full border border-amber-300 bg-white px-2.5 py-0.5 text-xs hover:bg-amber-100" data-testid={`crm-digest-client-${x.name}`}>
        {x.name} · {x.days < 0 ? `overdue ${-x.days}d` : `${fdate(x.due)}`}</button>))}</div>}
  </div>
);

export const CrmKpis = ({ k }) => (
  <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
    <Kpi icon={Users} k="Clients" v={k.clients} tone="bg-teal-50 text-teal-700" testid="crm-kpi-clients" />
    <Kpi icon={Pill} k="Refills due (7 days)" v={k.refills_week} tone="bg-rose-50 text-rose-600" testid="crm-kpi-refills" />
    <Kpi icon={CalendarClock} k="Follow-ups due" v={k.followups_due} tone="bg-amber-50 text-amber-600" testid="crm-kpi-followups" />
    <Kpi icon={Wallet} k="Sales (12 months)" v={inr(k.sales)} tone="bg-sky-50 text-sky-700" testid="crm-kpi-sales" />
  </div>
);

export const PortalClients = ({ onReorder, onAlerts }) => {
  const [d, setD] = useState(null);
  const [digest, setDigest] = useState(null);
  const [view, setView] = useState("split");
  const [f, setF] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [modal, setModal] = useState(null);
  const load = () => papi("get", "/crm/clients").then((r) => { setD(r); setSel((s) => s || (window.innerWidth >= 768 ? r.clients[0]?.key : null)); onAlerts?.(); }).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); papi("get", "/crm/alerts").then((a) => setDigest(a.digest)).catch(() => {}); }, []);
  if (!d) return <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  const rows = applyFilter(d.clients, f, q, d.today);
  const c = d.clients.find((x) => x.key === sel);
  const closeDigest = () => papi("post", "/crm/alerts/seen").then(() => { setDigest(null); onAlerts?.(); }).catch(() => {});
  const remove = async () => {
    if (!window.confirm(`Remove ${c.name} from My clients? Past parcels stay as they are.`)) return;
    try { await papi("post", "/address-book/remove", { name: c.name }); toast.success(`${c.name} removed`); setSel(null); load(); } catch (e) { toast.error(errText(e)); }
  };
  const restore = async (name) => {
    try { await papi("post", "/address-book/remove", { name, restore: true }); toast.success(`${name} is back`); load(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="space-y-3" data-testid="portal-clients">
      <div className="flex flex-wrap items-end gap-2">
        <div><h1 className="text-xl font-bold text-slate-800">My clients</h1><p className="text-xs text-slate-500">Contacts, orders, refills and follow-ups. Only you (and the ERP admins) can see this.</p></div>
        <div className="ml-auto flex rounded-lg border bg-white p-0.5">{[["split", LayoutList, "Profiles"], ["table", Table2, "Table"]].map(([k, I, l]) => (
          <button key={k} onClick={() => setView(k)} className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${view === k ? "bg-[#0f1c2e] text-white" : "text-slate-600"}`} data-testid={`crm-view-${k}`}><I className="h-3.5 w-3.5" />{l}</button>))}</div>
      </div>
      {digest && <Digest d={digest} onClose={closeDigest} onPick={(k) => { setSel(k); setView("split"); }} />}
      <CrmKpis k={d.kpis} />
      {view === "table" ? <><FilterBar f={f} setF={setF} q={q} setQ={setQ} onAdd={() => setModal("new")} /><ClientTable rows={rows} onView={(k) => { setSel(k); setView("split"); }} /></> : (
        <div className="grid gap-3 md:grid-cols-[340px_1fr]">
          <div className={`space-y-2 ${c ? "hidden md:block" : ""}`}><FilterBar f={f} setF={setF} q={q} setQ={setQ} onAdd={() => setModal("new")} /><ClientList rows={rows} sel={sel} onPick={setSel} /></div>
          {c && <ClientProfile c={c} today={d.today} onBack={() => setSel(null)} onTemplate={() => setModal("tpl")} onFollowUp={() => setModal("fu")}
            onEdit={() => setModal("edit")} onRemove={remove} onReorder={onReorder} onChanged={load} />}
        </div>)}
      {d.removed.length > 0 && (
        <div className="rounded-xl border border-dashed p-3 text-xs" data-testid="crm-removed">
          <p className="mb-1 font-semibold text-slate-600">Removed ({d.removed.length})</p>
          <div className="flex flex-wrap gap-2">{d.removed.map((r) => (
            <button key={r.key} onClick={() => restore(r.name)} className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 hover:bg-slate-50" data-testid={`crm-restore-${r.key}`}>{r.name} <RotateCcw className="h-3 w-3" /></button>))}</div>
        </div>)}
      {modal === "tpl" && c && <TemplateModal c={c} onClose={() => setModal(null)} />}
      {modal === "fu" && c && <FollowUpModal c={c} onClose={() => setModal(null)} onSaved={load} />}
      {(modal === "new" || modal === "edit") && <ClientModal c={modal === "edit" ? c : null} onClose={() => setModal(null)} onSaved={(k) => { setSel(k); load(); }} />}
    </div>
  );
};
